import { useEffect, useMemo, useState } from "react"
import { unwrapResult } from "@reduxjs/toolkit"
import { useLocalization } from "@fluent/react"
import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    TextField
} from "@mui/material"
import {
    CompanyDocumentType,
    createCompanyDocumentType,
    SaveCompanyDocumentType,
    updateCompanyDocumentType
} from "../companyDocumentTypesApi"
import { useAppDispatch } from "../../../../app/hooks"
import { APIError } from "../../../../app/appTypes"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"

interface AddEditCompanyDocumentTypeDialogProps {
    documentType?: CompanyDocumentType
    mode: "create" | "edit"
    nextDisplayOrder?: number
    onSuccessfullySaved: (documentType: CompanyDocumentType) => void
    onCancel: () => void
}

export const AddEditCompanyDocumentTypeDialog = ({
    documentType,
    mode,
    nextDisplayOrder,
    onSuccessfullySaved,
    onCancel
}: AddEditCompanyDocumentTypeDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const initialForm = useMemo<SaveCompanyDocumentType>(() => ({
        code: documentType?.code ?? "",
        title: documentType?.title ?? "",
        description: documentType?.description ?? "",
        isActive: documentType?.isActive ?? true,
        displayOrder: documentType?.displayOrder ?? (nextDisplayOrder ?? 0),
        color: documentType?.color ?? "#1976d2",
        isQmsEnabled: documentType?.isQmsEnabled ?? false,
    }), [documentType, nextDisplayOrder])

    const [form, setForm] = useState<SaveCompanyDocumentType>(initialForm)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<APIError | undefined>(undefined)

    useEffect(() => {
        setForm(initialForm)
    }, [initialForm])

    const handleSave = async () => {
        setIsSaving(true)
        setError(undefined)

        try {
            const dto: SaveCompanyDocumentType = {
                ...form,
                code: form.code.trim().toUpperCase(),
                title: form.title.trim(),
                description: form.description?.trim() ?? "",
            }

            if (mode === "create") {
                const result = await dispatch(createCompanyDocumentType(dto))
                const payload = unwrapResult(result)
                onSuccessfullySaved(payload)
            } else {
                if (!documentType?.id) {
                    throw { kind: "unknown" } as APIError
                }

                const result = await dispatch(updateCompanyDocumentType({ id: documentType.id, dto }))
                const payload = unwrapResult(result)
                onSuccessfullySaved(payload)
            }
        } catch (err) {
            setError(err as APIError)
        } finally {
            setIsSaving(false)
        }
    }

    const isSaveDisabled =
        isSaving ||
        !form.code.trim() ||
        !form.title.trim()

    return (
        <Dialog open={true} fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                {mode === "create"
                    ? l10n.getString("company-document-type-add-title", null, "Dodaj typ dokumentu")
                    : l10n.getString("company-document-type-edit-title", null, "Edytuj typ dokumentu")}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    {error && (
                        <Alert severity="error">
                            {l10n.getString("error", null, "Wystąpił błąd podczas zapisu.")}
                        </Alert>
                    )}

                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField
                            label={l10n.getString("company-document-type-code", null, "Kod")}
                            value={form.code}
                            onChange={e =>
                                setForm(prev => ({
                                    ...prev,
                                    code: e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 20)
                                }))
                            }
                            required
                            inputProps={{ maxLength: 20 }}
                            sx={{ width: 160 }}
                            disabled={mode === "edit"}
                        />

                        <TextField
                            label={l10n.getString("company-document-type-title", null, "Tytuł")}
                            value={form.title}
                            onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                            fullWidth
                            required
                            inputProps={{ maxLength: 200 }}
                        />
                    </Box>

                    <TextField
                        label={l10n.getString("description", null, "Opis")}
                        value={form.description ?? ""}
                        onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={3}
                        inputProps={{ maxLength: 500 }}
                    />

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <TextField
                            label={l10n.getString("company-document-type-display-order", null, "Kolejność")}
                            type="number"
                            value={form.displayOrder}
                            onChange={e => setForm(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                            sx={{ width: 140 }}
                            inputProps={{ min: 0 }}
                        />

                        <TextField
                            label={l10n.getString("company-document-type-color", null, "Kolor")}
                            value={form.color ?? "#1976d2"}
                            onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
                            sx={{ width: 200 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: "50%",
                                                bgcolor: form.color || "#1976d2",
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <input
                                            type="color"
                                            value={form.color || "#1976d2"}
                                            onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
                                            style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", padding: 0 }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {mode === "edit" && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.isActive}
                                    onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                />
                            }
                            label={l10n.getString("company-document-type-is-active", null, "_Active")}
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.isQmsEnabled}
                                onChange={e => {
                                    const checked = e.target.checked
                                    setForm(prev => ({ ...prev, isQmsEnabled: checked }))
                                }}
                            />
                        }
                        label={l10n.getString("company-document-type-is-qms-enabled", null, "_QMS enabled")} />
                </Box>
            </DialogContent>

            <DialogActions>
                <SaveButton onClick={handleSave} disabled={isSaveDisabled} loading={isSaving} />
                <CancelButton onClick={onCancel} loading={isSaving} />
            </DialogActions>
        </Dialog>
    )
}
